import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  serverTimestamp,
  increment,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';
import { createNotification } from './useNotifications';
import { callCancelPaymentHold } from './useStripe';

const PAGE_SIZE = 20;

/**
 * Hook for fetching bounties with cursor-based pagination (browse view)
 */
export const useBounties = (filters = {}) => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const lastDocRef = useRef(null);

  // Load initial page
  useEffect(() => {
    setLoading(true);
    setBounties([]);
    lastDocRef.current = null;
    setHasMore(true);

    const constraints = [
      where('status', '==', filters.status || 'open'),
      orderBy('createdAt', 'desc'),
      limit(PAGE_SIZE),
    ];

    const q = query(collection(db, 'bounties'), ...constraints);

    getDocs(q)
      .then((snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setBounties(data);
        lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
        setHasMore(snapshot.docs.length === PAGE_SIZE);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching bounties:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [filters.status]);

  // Load next page
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !lastDocRef.current) return;
    setLoadingMore(true);

    const constraints = [
      where('status', '==', filters.status || 'open'),
      orderBy('createdAt', 'desc'),
      startAfter(lastDocRef.current),
      limit(PAGE_SIZE),
    ];

    try {
      const snapshot = await getDocs(query(collection(db, 'bounties'), ...constraints));
      const newData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setBounties((prev) => [...prev, ...newData]);
      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] || null;
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Error loading more bounties:', err);
      setError(err.message);
    }
    setLoadingMore(false);
  }, [loadingMore, hasMore, filters.status]);

  return { bounties, loading, loadingMore, hasMore, loadMore, error };
};

/**
 * Hook for fetching a single bounty by ID
 */
export const useBounty = (bountyId) => {
  const [bounty, setBounty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bountyId) return;

    const docRef = doc(db, 'bounties', bountyId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          setBounty({ id: docSnap.id, ...docSnap.data() });
        } else {
          setBounty(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [bountyId]);

  return { bounty, loading, error };
};

/**
 * Hook for fetching bounties created by the current user
 */
export const useMyBounties = (uid) => {
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'bounties'),
      where('creatorUid', '==', uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setBounties(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching my bounties:', err);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [uid]);

  return { bounties, loading };
};

/**
 * Create a new bounty in Firestore
 */
export const createBounty = async (bountyData, uid, anonymizedUsername) => {
  const docRef = await addDoc(collection(db, 'bounties'), {
    ...bountyData,
    creatorUid: uid,
    creatorAnonymizedUsername: anonymizedUsername,
    status: 'open',
    viewCount: 0,
    claimCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
};

/**
 * Update a bounty's status
 */
export const updateBountyStatus = async (bountyId, status) => {
  await updateDoc(doc(db, 'bounties', bountyId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Increment bounty view count
 */
export const incrementViewCount = async (bountyId) => {
  await updateDoc(doc(db, 'bounties', bountyId), {
    viewCount: increment(1),
  });
};

/**
 * Update editable fields on an open bounty
 */
export const updateBounty = async (bountyId, fields) => {
  await updateDoc(doc(db, 'bounties', bountyId), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Cancel a bounty. If the bounty is in_review, also reject the pending claim.
 */
export const cancelBounty = async (bountyId) => {
  const bountySnap = await getDoc(doc(db, 'bounties', bountyId));
  if (!bountySnap.exists()) return;
  const bounty = bountySnap.data();

  await updateDoc(doc(db, 'bounties', bountyId), {
    status: 'cancelled',
    updatedAt: serverTimestamp(),
  });

  // Cancel Stripe hold if one exists
  if (bounty.stripeHoldPaymentIntentId) {
    try {
      await callCancelPaymentHold(bountyId);
    } catch (err) {
      console.error('Failed to cancel payment hold:', err);
    }
  }

  // If there's a pending claim, reject it and notify researcher
  if (bounty.status === 'in_review') {
    const claimsSnap = await getDocs(
      query(collection(db, 'claims'), where('bountyId', '==', bountyId), where('status', '==', 'pending'))
    );
    for (const claimDoc of claimsSnap.docs) {
      const claimData = claimDoc.data();
      await updateDoc(claimDoc.ref, {
        status: 'rejected',
        reviewedAt: serverTimestamp(),
      });

      // Notify researcher of bounty cancellation (non-blocking)
      try {
        await createNotification(
          claimData.claimantUid,
          'bounty_cancelled',
          'Bounty Cancelled',
          `The bounty "${bounty.title}" has been cancelled by the creator.`,
          bountyId,
          bounty.title,
          claimDoc.id,
          `/submissions/${claimDoc.id}`
        );
      } catch (err) {
        console.error('Failed to send notification:', err);
      }
    }
  }
};

/**
 * Hook for fetching a public user profile by UID (one-time read)
 */
export const usePublicUser = (userId) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    getDoc(doc(db, 'users', userId)).then((snap) => {
      setUser(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  return { user, loading };
};
